<script setup lang="ts">
import { useClipboard } from '@vueuse/core'

import type { OAuthConnection } from '../../model/oauth/interfaces'

import { AppText } from '@shared/ui/typography'
import { useToast } from '@shared/ui/toast'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useOAuthDisconnect } from '../../api/oauth/disconnect.hook'

defineOptions({
  name: 'CardOAuthConnection',
})

const props = defineProps<CardOAuthConnectionProps>()

const emit = defineEmits<{
  disconnected: []
}>()

interface CardOAuthConnectionProps {
  title: string
  tokenId: string
  connection: OAuthConnection
}

const toast = useToast()
const { copy, isSupported } = useClipboard()
const { isRunning, canDisconnect, disconnect } = useOAuthDisconnect()

const handleConnect = (): void => {
  window.location.href = props.connection.url
}

const handleDisconnect = async (): Promise<void> => {
  const ok = await disconnect(props.tokenId)

  if (ok) {
    emit('disconnected')
  }
}

const handleCopy = async (): Promise<void> => {
  await copy(props.connection.redirect_url)
  toast.success('Redirect URL copied')
}
</script>

<template>
  <CardAdmin
    :title="title"
    fullwidth
  >
    <div class="oauth-connection">
      <div class="oauth-connection__status">
        <AppText
          name="body1"
          color="slate-900"
        >
          Status:
        </AppText>
        <AppText
          name="body1-medium"
          :color="connection.connected ? 'emerald-600' : 'red-500'"
        >
          {{ connection.connected ? 'Connected' : 'Disconnected' }}
        </AppText>
      </div>
      <div class="oauth-connection__actions">
        <BtnPrimary
          v-if="connection.connected"
          text="Disconnect"
          color="red"
          :disabled="!canDisconnect || isRunning"
          @click="handleDisconnect"
        />
        <BtnPrimary
          v-else
          text="Connect"
          @click="handleConnect"
        />
        <BtnSecondary
          v-if="isSupported"
          text="Copy redirect URL"
          @click="handleCopy"
        />
      </div>
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./CardOAuthConnection.scss" />
